import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";

import { userService } from "../../_services";

function buildEntries(all_requisites, required_fields) {
  if (Array.isArray(required_fields) && required_fields.length > 0) {
    return required_fields.map((f) => ({
      key: String(f.req_id),
      label: f.label,
    }));
  }
  if (!all_requisites) return [];
  return Object.keys(all_requisites).map((key) => ({
    key: String(key),
    label: all_requisites[key],
  }));
}

function buildPayload(entries, values, current_requisites) {
  const existing = {};
  if (Array.isArray(current_requisites)) {
    current_requisites.forEach((r) => {
      existing[String(r.requisite_key_id)] = r;
    });
  }

  const payload = [];
  entries.forEach((e) => {
    const newVal = (values[e.key] || "").trim();
    const old = existing[e.key];
    if (newVal === "" && !old) return;

    const item = {
      requisite_key_id: e.key,
      value: newVal,
      action: old ? "update" : "insert",
    };
    if (old) item.id = old.id;
    payload.push(item);
  });
  return payload;
}

export default function RequisitesDialog(props) {
  const { open, onClose, onSaved, mandatory, all_requisites, current_requisites, required_fields } = props;

  const { t } = useTranslation();

  const entries = React.useMemo(
    () => buildEntries(all_requisites, required_fields),
    [all_requisites, required_fields]
  );

  const [values, setValues] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    const init = {};
    if (Array.isArray(current_requisites)) {
      current_requisites.forEach((r) => {
        init[String(r.requisite_key_id)] = r.value ?? "";
      });
    }
    entries.forEach((e) => {
      if (init[e.key] === undefined) init[e.key] = "";
    });
    setValues(init);
    setApiError("");
  }, [open, current_requisites, entries]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;

    setLoading(true);
    setApiError("");

    userService.patchWrapperBody(
      "api/user",
      { requisites: buildPayload(entries, values, current_requisites) },
      (data) => {
        setLoading(false);
        if (onSaved) onSaved(data);
      },
      (error) => {
        setLoading(false);
        const message =
          (error && error.response && error.response.data && error.response.data.message) ||
          t("cant_execute_request");
        setApiError(message);
      }
    );
  };

  const handleCancel = () => {
    if (loading) return;
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{t("requisites_dialog_title")}</DialogTitle>
      <form onSubmit={handleSave}>
        {/* См. BldDialog: жёсткая ширина не помещается в экран телефона. */}
        <DialogContent style={{ width: "100%", maxWidth: 500 }}>
          {mandatory && (
            <DialogContentText style={{ marginBottom: 12 }}>
              {t("requisites_publish_required")}
            </DialogContentText>
          )}
          {entries.length === 0 && (
            <DialogContentText style={{ color: "#999" }}>…</DialogContentText>
          )}
          {entries.map((e, idx) => (
            <TextField
              key={e.key}
              autoFocus={idx === 0}
              margin="dense"
              id={`requisite-${e.key}`}
              label={e.label}
              fullWidth
              variant="standard"
              value={values[e.key] || ""}
              onChange={(ev) => handleChange(e.key, ev.target.value)}
              disabled={loading}
            />
          ))}
          {apiError && (
            <DialogContentText style={{ color: "#d32f2f", marginTop: 8 }}>
              {apiError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions style={{ padding: 16, display: "block" }}>
          {loading && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
              <CircularProgress size={24} />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCancel} disabled={loading}>
              {t("btn_cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading}
            >
              {t("btn_save")}
            </Button>
          </div>
          {mandatory && (
            <DialogContentText
              variant="caption"
              style={{ marginTop: 8, color: "#777", display: "block" }}
            >
              {t("requisites_cancel_publish_hint")}
            </DialogContentText>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
